import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, Chip } from "@mui/material";
import { TrendingUp, TrendingDown } from "@mui/icons-material";
import type { SxProps, Theme } from "@mui/material";

interface StatsCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  backgroundColor: string;
  textColor?: string;
  trend?: number;
  subtitle?: string;
  sx?: SxProps<Theme>;
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  icon,
  backgroundColor,
  trend,
  subtitle,
  sx,
}) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 1200;
    const increment = value / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: "100%",
        borderRadius: 2,
        background: `linear-gradient(135deg, ${backgroundColor}15 0%, ${backgroundColor}05 100%)`,
        border: "1px solid",
        borderColor: `${backgroundColor}30`,
        position: "relative",
        overflow: "hidden",
        minWidth: "180px",
        flex: 1,
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 8px 16px ${backgroundColor}25`,
          borderColor: backgroundColor,
          "& .metric-icon": {
            transform: "rotate(10deg) scale(1.1)",
          },
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: -30,
          right: -30,
          width: "100px",
          height: "100px",
          background: `radial-gradient(circle, ${backgroundColor}20 0%, transparent 70%)`,
          borderRadius: "50%",
        },
        ...sx,
      }}
    >
      
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
                fontWeight: 500,
                fontSize: "0.65rem",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box
          className="metric-icon"
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${backgroundColor} 0%, ${backgroundColor}DD 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 4px 8px ${backgroundColor}40`,
            transition: "transform 0.4s ease",
            color: "white",
            "& svg": { fontSize: 22 },
          }}
        >
          {icon}
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: backgroundColor,
            fontSize: "2.5rem",
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          {displayValue.toLocaleString()}
        </Typography>

        {trend !== undefined && (
          <Chip
            icon={trend >= 0 ? <TrendingUp /> : <TrendingDown />}
            label={`${trend >= 0 ? "+" : ""}${trend}%`}
            size="small"
            sx={{
              backgroundColor: trend >= 0 ? "#10b98125" : "#ef444425",
              color: trend >= 0 ? "#10b981" : "#ef4444",
              fontWeight: 700,
              fontSize: "0.65rem",
              "& .MuiChip-icon": { color: "inherit" },
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${backgroundColor} 0%, ${backgroundColor}60 100%)`,
          opacity: 0.6,
        }}
      />
    </Paper>
  );
};

export default StatsCard;